import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import { useLoaderData, useNavigate } from "react-router-dom";
import { Paper } from "@mui/material";
import { Result } from "../../types/Result";
import { EmailLog } from "./type";
import { retryEmailLog } from "./laoder";
import { toast } from "react-toastify";
import { useState } from "react";
import SendIcon from "@mui/icons-material/Send";

const EmailLogDetailItem = ({
  label,
  content,
  isHtml = false,
}: {
  label: string;
  content: string | null;
  isHtml?: boolean;
}) => {
  if (!content) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {isHtml ? (
        <Paper
          elevation={0}
          sx={{ p: 2, bgcolor: "#f5f5f5", maxHeight: 400, overflow: "auto" }}
        >
          <div dangerouslySetInnerHTML={{ __html: content }} />
        </Paper>
      ) : (
        <Typography
          variant="body2"
          component="pre"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            p: 2,
            borderRadius: 1,
            fontFamily: "monospace",
          }}
        >
          {content}
        </Typography>
      )}
    </Box>
  );
};

export default function EmailLogForm() {
  const data = useLoaderData() as Result<EmailLog>;
  const [log, setLog] = useState<EmailLog>(data.data!);
  const [isLoading, setIsLoading] = useState(false);

  const handleRetry = async () => {
    try {
      setIsLoading(true);
      const res = await retryEmailLog(log.id);
      if (res.data.isSuccess) {
        setLog(res.data.data!);
        toast.success("E-posta başarıyla tekrar gönderildi.");
      } else {
        toast.error(
          "Gönderim başarısız: " + (res.data.errors?.[0] || "Bilinmeyen hata"),
        );
      }
    } catch (e: any) {
      toast.error("Bir hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Card elevation={3}>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            mb={2}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                label={log.isSent ? "Gönderildi" : "Başarısız"}
                color={log.isSent ? "success" : "error"}
                size="small"
                sx={{ fontWeight: "bold" }}
              />
              <Typography variant="caption" color="text.secondary">
                Oluşturulma: {new Date(log.creationTime).toLocaleString()}
              </Typography>
              {log.sentTime && (
                <Typography variant="caption" color="text.secondary">
                  Gönderilme: {new Date(log.sentTime).toLocaleString()}
                </Typography>
              )}
            </Stack>
            {!log.isSent && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleRetry}
                disabled={isLoading}
              >
                Tekrar Gönder
              </Button>
            )}
          </Stack>

          <Typography variant="h6" gutterBottom>
            Alıcı: {log.toEmail}
          </Typography>
          <Typography variant="subtitle1" gutterBottom color="textSecondary">
            Konu: {log.subject}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Stack spacing={3}>
            {log.errorMessage && (
              <EmailLogDetailItem
                label="Hata Mesajı"
                content={log.errorMessage}
              />
            )}
            <EmailLogDetailItem
              label="Gönderim Tekrarı"
              content={log.retryCount.toString()}
            />
            {log.nextRetryTime && (
              <EmailLogDetailItem
                label="Sonraki Otomatik Deneme"
                content={new Date(log.nextRetryTime).toLocaleString()}
              />
            )}
            <EmailLogDetailItem
              label="İçerik (HTML)"
              content={log.body}
              isHtml={true}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
